import { useState, useEffect } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { 
  Users, Database, Camera, Shield, Eye, Clock, Bell, 
  BarChart3, UserCheck, AlertTriangle, MapPin, Download, Search,
  Plus, Edit, Trash2, Monitor, Activity, Wrench, Wifi, WifiOff, LogOut, ScanLine
} from 'lucide-react';
import { useAdminSession } from '@/hooks/useAdminSession';
import ClientManagement from '@/components/admin/ClientManagement';
import CameraManagement from '@/components/admin/CameraManagement';
import UserPermissions from '@/components/admin/UserPermissions';
import PlanoManagement from '@/components/admin/PlanoManagement';
import PlateCaptureReport from '@/components/admin/PlateCaptureReport';
import FaceCaptureReport from '@/components/admin/FaceCaptureReport';
import { API_BASE_URL } from '@/lib/authFetch';
import { CreditCard } from 'lucide-react';

// ========================================
// FUNCIONALIDADES DESABILITADAS TEMPORARIAMENTE
// Serão implementadas em versões futuras
// ========================================
// import SystemLogs from '@/components/admin/SystemLogs';
// import AdminReports from '@/components/admin/AdminReports';
// import AdminAlerts from '@/components/admin/AdminAlerts';
// import SystemMaintenance from '@/components/admin/SystemMaintenance';

const AdminDashboard = () => {
  // Hook de sessão admin (gerencia expiração e renovação automática do token)
  const { logout } = useAdminSession();
  
  const [activeTab, setActiveTab] = useState("overview");
  const [showLogoutModal, setShowLogoutModal] = useState(false);
  
  // Função para confirmar logout
  const handleConfirmLogout = () => {
    setShowLogoutModal(false);
    logout();
  };
  
  // Obter dados do usuário logado
  const [userRole, setUserRole] = useState<string>('operator');
  const [userName, setUserName] = useState<string>('');
  
  // Estatísticas reais
  const [stats, setStats] = useState({
    totalClientes: 0,
    camerasOnline: 0,
    camerasOffline: 0,
    consultasHoje: 0,
    alertasAtivos: 0
  });
  const [loadingStats, setLoadingStats] = useState(true);
  
  useEffect(() => {
    const adminUser = localStorage.getItem('admin_user');
    if (adminUser) {
      const user = JSON.parse(adminUser);
      setUserRole(user.role || 'operator');
      setUserName(user.nome || '');
    }
    
    // Carregar estatísticas reais
    fetchStats();
  }, []);

  const fetchStats = async () => {
    try {
      const token = localStorage.getItem('admin_token');
      const headers = token ? { 'Authorization': `Bearer ${token}` } : {};
      
      // Buscar clientes
      const clientsRes = await fetch(`${API_BASE_URL}/admin/clients`, { headers });
      const clients = clientsRes.ok ? await clientsRes.json() : [];
      
      // Buscar câmeras
      const camerasRes = await fetch(`${API_BASE_URL}/admin/cameras`, { headers });
      const cameras = camerasRes.ok ? await camerasRes.json() : [];
      
      setStats({
        totalClientes: Array.isArray(clients) ? clients.length : 0,
        camerasOnline: Array.isArray(cameras) ? cameras.filter((c: any) => c.status === 'online').length : 0,
        camerasOffline: Array.isArray(cameras) ? cameras.filter((c: any) => c.status !== 'online').length : 0,
        consultasHoje: Array.isArray(clients) ? clients.reduce((acc: number, c: any) => acc + (c.consultas || 0), 0) : 0,
        alertasAtivos: 0 // Será implementado depois com endpoint admin
      });
    } catch {
      // Erro ao carregar estatísticas
    } finally {
      setLoadingStats(false);
    }
  };

  // Mapeamento de roles para exibição
  const roleLabels: Record<string, string> = {
    'admin': 'Administrador',
    'operator': 'Operador'
  };

  // Permissões por role
  // Admin: tudo | Operador: Visão Geral, Clientes, Câmeras
  // Se não for admin, é operador (tem acesso a clientes e câmeras)
  const isAdmin = userRole === 'admin';
  const canAccessClients = true; // Todos podem ver clientes
  const canAccessCameras = true; // Todos podem ver câmeras
  const canAccessUsers = isAdmin;
  const canAccessSettings = isAdmin;

  return (
    <div className="min-h-screen bg-gradient-to-br from-protec-dark via-protec-dark-light to-protec-dark-lighter text-white">
      {/* Admin Header */}
      <header className="border-b border-protec-gold/20 bg-black/20 backdrop-blur-md sticky top-0 z-50">
        <div className="container mx-auto px-3 sm:px-4 py-3 sm:py-4 flex items-center justify-between">
          <div className="flex items-center space-x-2 sm:space-x-3">
            <img 
              src="/logo.png" 
              alt="Protec Bens" 
              className="h-8 w-8 sm:h-10 sm:w-10"
            />
            <div>
              <h1 className="text-sm sm:text-xl font-bold gradient-text">
                <span className="hidden sm:inline">PAINEL ADMINISTRATIVO</span>
                <span className="sm:hidden">ADMIN</span>
              </h1>
            </div>
          </div>
          
          <div className="flex items-center space-x-2 sm:space-x-4">
            <Badge className="bg-protec-gold/20 text-protec-gold border-protec-gold/30 text-xs sm:text-sm">
              <Shield className="w-3 h-3 mr-1" />
              <span className="hidden sm:inline">{roleLabels[userRole] || 'Operador'}</span>
              <span className="sm:hidden">{userRole === 'admin' ? 'Adm' : 'Op'}</span>
            </Badge>
            <Button 
              variant="outline" 
              size="sm"
              onClick={() => setShowLogoutModal(true)}
              className="border-gray-500/30 text-gray-400 hover:bg-gray-500/20 hover:text-white h-8 px-2 sm:px-3"
            >
              <LogOut className="w-4 h-4 sm:mr-1" />
              <span className="hidden sm:inline">Sair</span>
            </Button>
          </div>
        </div>
      </header>

      <div className="container mx-auto px-3 sm:px-4 py-4 sm:py-6">
        <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
          <TabsList className={`grid w-full bg-protec-dark-light mb-4 sm:mb-6 h-auto gap-1 p-1 ${
            isAdmin ? 'grid-cols-2 sm:grid-cols-4 lg:grid-cols-7' : 'grid-cols-2 sm:grid-cols-4'
          }`}>
            <TabsTrigger 
              value="overview" 
              className="data-[state=active]:bg-protec-gold data-[state=active]:text-black text-xs sm:text-sm py-2 px-1 sm:px-3"
            >
              <BarChart3 className="w-3 h-3 sm:w-4 sm:h-4 sm:mr-2" />
              <span className="hidden sm:inline">Visão Geral</span>
            </TabsTrigger>
            {canAccessClients && (
              <TabsTrigger 
                value="clients" 
                className="data-[state=active]:bg-protec-gold data-[state=active]:text-black text-xs sm:text-sm py-2 px-1 sm:px-3"
              >
                <Users className="w-3 h-3 sm:w-4 sm:h-4 sm:mr-2" />
                <span className="hidden sm:inline">Clientes</span>
              </TabsTrigger>
            )}
            {canAccessCameras && (
              <TabsTrigger 
                value="cameras" 
                className="data-[state=active]:bg-protec-gold data-[state=active]:text-black text-xs sm:text-sm py-2 px-1 sm:px-3"
              >
                <Camera className="w-3 h-3 sm:w-4 sm:h-4 sm:mr-2" />
                <span className="hidden sm:inline">Câmeras</span>
              </TabsTrigger>
            )}
            {canAccessCameras && (
              <TabsTrigger
                value="plate-report"
                className="data-[state=active]:bg-protec-gold data-[state=active]:text-black text-xs sm:text-sm py-2 px-1 sm:px-3"
              >
                <ScanLine className="w-3 h-3 sm:w-4 sm:h-4 sm:mr-2" />
                <span className="hidden sm:inline">Placas</span>
              </TabsTrigger>
            )}
            {canAccessCameras && (
              <TabsTrigger
                value="face-report"
                className="data-[state=active]:bg-protec-gold data-[state=active]:text-black text-xs sm:text-sm py-2 px-1 sm:px-3"
              >
                <Users className="w-3 h-3 sm:w-4 sm:h-4 sm:mr-2" />
                <span className="hidden sm:inline">Facial</span>
              </TabsTrigger>
            )}
            {canAccessUsers && (
              <TabsTrigger 
                value="users" 
                className="data-[state=active]:bg-protec-gold data-[state=active]:text-black text-xs sm:text-sm py-2 px-1 sm:px-3"
              >
                <Shield className="w-3 h-3 sm:w-4 sm:h-4 sm:mr-2" />
                <span className="hidden sm:inline">Usuários</span>
              </TabsTrigger>
            )}
            {isAdmin && (
              <TabsTrigger 
                value="planos" 
                className="data-[state=active]:bg-protec-gold data-[state=active]:text-black text-xs sm:text-sm py-2 px-1 sm:px-3"
              >
                <CreditCard className="w-3 h-3 sm:w-4 sm:h-4 sm:mr-2" />
                <span className="hidden sm:inline">Planos</span>
              </TabsTrigger>
            )}
            {/* Configurações removida temporariamente */}
            
            {/* ========================================
            TABS DESABILITADAS TEMPORARIAMENTE
            ========================================
            <TabsTrigger value="logs" className="data-[state=active]:bg-protec-gold data-[state=active]:text-black">
              <Clock className="w-4 h-4 mr-2" />
              Logs
            </TabsTrigger>
            <TabsTrigger value="reports" className="data-[state=active]:bg-protec-gold data-[state=active]:text-black">
              <Download className="w-4 h-4 mr-2" />
              Relatórios
            </TabsTrigger>
            <TabsTrigger value="alerts" className="data-[state=active]:bg-protec-gold data-[state=active]:text-black">
              <Bell className="w-4 h-4 mr-2" />
              Alertas
            </TabsTrigger>
            <TabsTrigger value="maintenance" className="data-[state=active]:bg-protec-gold data-[state=active]:text-black">
              <Wrench className="w-4 h-4 mr-2" />
              Manutenção
            </TabsTrigger>
            <TabsTrigger value="system" className="data-[state=active]:bg-protec-gold data-[state=active]:text-black">
              <Monitor className="w-4 h-4 mr-2" />
              Sistema
            </TabsTrigger>
            ======================================== */}
          </TabsList>

          <TabsContent value="overview">
            <div className="space-y-4 sm:space-y-6">
              {/* Stats Grid - Dados Reais */}
              <div className="grid grid-cols-2 md:grid-cols-2 lg:grid-cols-4 gap-2 sm:gap-4">
                <Card className="bg-protec-dark-light border-protec-gold/20">
                  <CardContent className="p-3 sm:p-6">
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="text-xs sm:text-sm text-gray-400">Clientes</p>
                        <p className="text-lg sm:text-2xl font-bold text-white">
                          {loadingStats ? '...' : stats.totalClientes}
                        </p>
                      </div>
                      <Users className="w-4 h-4 sm:w-5 sm:h-5 text-green-400" />
                    </div>
                  </CardContent>
                </Card>

                <Card className="bg-protec-dark-light border-protec-gold/20">
                  <CardContent className="p-3 sm:p-6">
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="text-xs sm:text-sm text-gray-400">Câmeras</p>
                        <p className="text-lg sm:text-2xl font-bold text-white">
                          {loadingStats ? '...' : `${stats.camerasOnline}/${stats.camerasOnline + stats.camerasOffline}`}
                        </p>
                      </div>
                      <Camera className="w-4 h-4 sm:w-5 sm:h-5 text-blue-400" />
                    </div>
                  </CardContent>
                </Card>

                <Card className="bg-protec-dark-light border-protec-gold/20">
                  <CardContent className="p-3 sm:p-6">
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="text-xs sm:text-sm text-gray-400">Consultas</p>
                        <p className="text-lg sm:text-2xl font-bold text-white">
                          {loadingStats ? '...' : stats.consultasHoje}
                        </p>
                      </div>
                      <Search className="w-4 h-4 sm:w-5 sm:h-5 text-purple-400" />
                    </div>
                  </CardContent>
                </Card>

                <Card className="bg-protec-dark-light border-protec-gold/20">
                  <CardContent className="p-3 sm:p-6">
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="text-xs sm:text-sm text-gray-400">Alertas</p>
                        <p className="text-lg sm:text-2xl font-bold text-white">
                          {loadingStats ? '...' : stats.alertasAtivos}
                        </p>
                      </div>
                      <Bell className="w-4 h-4 sm:w-5 sm:h-5 text-orange-400" />
                    </div>
                  </CardContent>
                </Card>
              </div>

              {/* Saúde do Sistema & Atividades Recentes */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4 sm:gap-6">
                <Card className="bg-protec-dark-light border-protec-gold/20">
                  <CardHeader className="pb-2 sm:pb-4">
                    <CardTitle className="text-white flex items-center gap-2 text-base sm:text-lg">
                      <Monitor className="w-4 h-4 sm:w-5 sm:h-5 text-protec-gold" />
                      Saúde do Sistema
                    </CardTitle>
                    <CardDescription className="text-gray-300 text-xs sm:text-sm">
                      Status dos componentes principais
                    </CardDescription>
                  </CardHeader>
                  <CardContent>
                    <div className="space-y-3 sm:space-y-4">
                      <div className="flex items-center justify-between gap-2">
                        <div className="min-w-0 flex-1">
                          <h4 className="text-white font-medium text-sm sm:text-base truncate">Backend API</h4>
                          <p className="text-gray-400 text-xs sm:text-sm">Servidor principal</p>
                        </div>
                        <Badge className="bg-green-500/20 text-green-400 text-xs flex-shrink-0">Online</Badge>
                      </div>
                      <div className="flex items-center justify-between gap-2">
                        <div className="min-w-0 flex-1">
                          <h4 className="text-white font-medium text-sm sm:text-base truncate">Banco de Dados</h4>
                          <p className="text-gray-400 text-xs sm:text-sm">PostgreSQL</p>
                        </div>
                        <Badge className="bg-green-500/20 text-green-400 text-xs flex-shrink-0">Online</Badge>
                      </div>
                      <div className="flex items-center justify-between gap-2">
                        <div className="min-w-0 flex-1">
                          <h4 className="text-white font-medium text-sm sm:text-base truncate">Sistema de Câmeras</h4>
                          <p className="text-gray-400 text-xs sm:text-sm">{stats.camerasOnline} online / {stats.camerasOffline} offline</p>
                        </div>
                        <Badge className={`text-xs flex-shrink-0 ${stats.camerasOffline > 0 ? "bg-yellow-500/20 text-yellow-400" : "bg-green-500/20 text-green-400"}`}>
                          {stats.camerasOffline > 0 ? 'Atenção' : 'Online'}
                        </Badge>
                      </div>
                      <div className="flex items-center justify-between gap-2">
                        <div className="min-w-0 flex-1">
                          <h4 className="text-white font-medium text-sm sm:text-base truncate">Processamento de IA</h4>
                          <p className="text-gray-400 text-xs sm:text-sm">Aguardando configuração</p>
                        </div>
                        <Badge className="bg-gray-500/20 text-gray-400 text-xs flex-shrink-0">Pendente</Badge>
                      </div>
                    </div>
                  </CardContent>
                </Card>

                <Card className="bg-protec-dark-light border-protec-gold/20">
                  <CardHeader className="pb-2 sm:pb-4">
                    <CardTitle className="text-white flex items-center gap-2 text-base sm:text-lg">
                      <Activity className="w-4 h-4 sm:w-5 sm:h-5 text-protec-gold" />
                      Atividades Recentes
                    </CardTitle>
                    <CardDescription className="text-gray-300 text-xs sm:text-sm">
                      Últimas ações no sistema
                    </CardDescription>
                  </CardHeader>
                  <CardContent>
                    <div className="space-y-3">
                      <div className="flex items-center gap-2 sm:gap-3">
                        <UserCheck className="w-4 h-4 text-gray-400 flex-shrink-0" />
                        <div className="flex-1 min-w-0">
                          <p className="text-white text-xs sm:text-sm truncate">Admin fez login no sistema</p>
                          <p className="text-gray-400 text-xs">{new Date().toLocaleDateString('pt-BR')} {new Date().toLocaleTimeString('pt-BR', {hour: '2-digit', minute: '2-digit'})}</p>
                        </div>
                      </div>
                      {stats.camerasOffline > 0 && (
                        <div className="flex items-center gap-2 sm:gap-3">
                          <Camera className="w-4 h-4 text-red-400 flex-shrink-0" />
                          <div className="flex-1 min-w-0">
                            <p className="text-white text-xs sm:text-sm truncate">{stats.camerasOffline} câmera(s) offline</p>
                            <p className="text-gray-400 text-xs">Verificar conexão</p>
                          </div>
                        </div>
                      )}
                      {stats.totalClientes > 0 && (
                        <div className="flex items-center gap-2 sm:gap-3">
                          <Users className="w-4 h-4 text-gray-400 flex-shrink-0" />
                          <div className="flex-1 min-w-0">
                            <p className="text-white text-xs sm:text-sm truncate">{stats.totalClientes} cliente(s) cadastrado(s)</p>
                            <p className="text-gray-400 text-xs">Total no sistema</p>
                          </div>
                        </div>
                      )}
                      {stats.consultasHoje > 0 && (
                        <div className="flex items-center gap-2 sm:gap-3">
                          <Search className="w-4 h-4 text-gray-400 flex-shrink-0" />
                          <div className="flex-1 min-w-0">
                            <p className="text-white text-xs sm:text-sm truncate">{stats.consultasHoje} consultas realizadas</p>
                            <p className="text-gray-400 text-xs">Total de buscas</p>
                          </div>
                        </div>
                      )}
                    </div>
                  </CardContent>
                </Card>
              </div>
            </div>
          </TabsContent>

          {canAccessClients && (
            <TabsContent value="clients">
              <ClientManagement />
            </TabsContent>
          )}

          {canAccessCameras && (
            <TabsContent value="cameras">
              <CameraManagement />
            </TabsContent>
          )}

          {canAccessCameras && (
            <TabsContent value="plate-report">
              <PlateCaptureReport />
            </TabsContent>
          )}

          {canAccessCameras && (
            <TabsContent value="face-report">
              <FaceCaptureReport />
            </TabsContent>
          )}

          {canAccessUsers && (
            <TabsContent value="users">
              <UserPermissions />
            </TabsContent>
          )}

          {isAdmin && (
            <TabsContent value="planos">
              <PlanoManagement />
            </TabsContent>
          )}

          {/* Configurações removida temporariamente */}

          {/* ========================================
          TABS DESABILITADAS TEMPORARIAMENTE
          Serão implementadas em versões futuras
          ========================================
          <TabsContent value="logs">
            <SystemLogs />
          </TabsContent>

          <TabsContent value="reports">
            <AdminReports />
          </TabsContent>

          <TabsContent value="alerts">
            <AdminAlerts />
          </TabsContent>

          <TabsContent value="maintenance">
            <SystemMaintenance />
          </TabsContent>

          <TabsContent value="system">
            <Card className="bg-protec-dark-light border-protec-gold/20">
              <CardHeader>
                <CardTitle className="text-white">Sistema</CardTitle>
              </CardHeader>
              <CardContent>
                <p>Em desenvolvimento</p>
              </CardContent>
            </Card>
          </TabsContent>
          ======================================== */}
        </Tabs>
      </div>

      {/* Modal de Confirmação de Logout */}
      <Dialog open={showLogoutModal} onOpenChange={setShowLogoutModal}>
        <DialogContent className="bg-protec-dark-light border-protec-gold/20 text-white max-w-sm">
          <DialogHeader>
            <DialogTitle className="text-white flex items-center gap-2">
              <LogOut className="w-5 h-5 text-protec-gold" />
              Confirmar Saída
            </DialogTitle>
          </DialogHeader>
          <p className="text-gray-300 py-4">
            Tem certeza que deseja sair do painel administrativo?
          </p>
          <DialogFooter className="flex gap-2">
            <Button 
              onClick={handleConfirmLogout}
              className="flex-1 bg-protec-gold text-black hover:bg-protec-gold-light"
            >
              Sair
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default AdminDashboard;
